import axios from 'axios';
import { env } from 'env';

type TimeTrackingRef = {
  [key: string]: {
    startTime: number;
  };
};

/**
 * @layer service
 * @feature analytics
 * 
 * @uses axios
 * @uses env
 * 
 * The `AnalyticsService` class is responsible for tracking and sending analytics events to a specified API endpoint.
 * It manages the lifecycle of analytics events, including start and finish events, and calculates the duration for paired events.
 */
export default class AnalyticsService {
  private static timeTrackingRef: TimeTrackingRef = {};

  /**
   * Sends an analytics event to the specified API endpoint.
   * 
   * This method constructs an analytics event object with the provided type, 
   * timestamp, and additional data, then sends it as a POST request to the 
   * configured API endpoint.
   * 
   * @param type - The type of the analytics event.
   * @param timestamp - The timestamp of the event.
   * @param data - Additional data to include in the event.
   * 
   * @uses axios - For making HTTP requests.
   * @uses env.REACT_APP_API_URL - The base URL of the API endpoint.
   */
  private static async sendAnalyticsEvent(
    type: string,
    timestamp: number,
    data: any,
  ): Promise<void> {
    await axios.post(`${env.REACT_APP_API_URL}api/v1/analytics`, {
      type,
      timestamp,
      ...data,
    });
  }

  /**
   * Tracks an event with the specified type and data.
   * 
   * This method handles different types of events: start, finish, and regular events.
   * For start events, it records the start time and expects a corresponding finish event.
   * For finish events, it calculates the duration since the start event and includes it in the data.
   * Regular events are tracked without any special handling.
   * 
   * @param type - The type of the event to track. This can be a start event, a finish event, or a regular event.
   * @param timestamp - The timestamp of when the event occurred.
   * @param data - The data associated with the event.
   * 
   * @uses sendAnalyticsEvent - Sends the analytics event to the appropriate service.
   */
  static async track(
    type: string,
    timestamp: number,
    data: any,
  ): Promise<void> {
    try {
      // Check if this is a finish event for a paired type
      if (
        (type.startsWith('FINISH') || type.endsWith('FINISH')) &&
        this.timeTrackingRef[type]
      ) {
        const startEvent = this.timeTrackingRef[type];
        const duration = new Date().getTime() - startEvent.startTime;

        const updatedData = {
          ...data,
          payload: { ...data.payload, duration },
        };

        console.log(
          `[AnalyticsService] ${type} [DURATION]: ${(duration / 1000).toFixed(
            2,
          )}s`,
        );

        await this.sendAnalyticsEvent(type, timestamp, {
          ...updatedData,
        });

        delete this.timeTrackingRef[type];
      }
      // Check if this is a start event for a paired type
      else if (type.startsWith('START') || type.endsWith('START')) {
        const finishType = type.replace('START', 'FINISH');
        this.timeTrackingRef[finishType] = {
          startTime: new Date().getTime(),
        };

        await this.sendAnalyticsEvent(type, timestamp, data);
      }
      // Regular event tracking
      else {
        await this.sendAnalyticsEvent(type, timestamp, data);
      }
    } catch (e) {
      console.error(`Can't track analytics: ${type}`);
    }
  }
}
